import React from 'react'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { BezierHandleDot } from '@/components/app/editor/tools/shared/components/bezier-handle-dot'
import { NODE_ACCENT_COLOR } from '../constants/node-chrome'
import type { DragTarget } from '../types/node-edit-targets'

interface NodeHandleDotsProps {
  points: PenAnchorPoint[]
  /** Shape origin the (shape-local) points are offset by. */
  ox: number
  oy: number
  /** Handle line stroke width (canvas units). */
  sw: number
  /** Handle dot radius (canvas units). */
  handleR: number
  /** Handle dot outline width (canvas units). */
  handleStrokeWidth: number
  /** Pen-edit sub-mode: handles are display-only. */
  isPenEditMode: boolean
  onElementDown: (e: React.PointerEvent, target: DragTarget) => void
  onElementMove: (e: React.PointerEvent) => void
  onElementUp: (e: React.PointerEvent) => void
}

/** Handle lines + circles for every anchor; draggable outside pen-edit mode. */
export function NodeHandleDots({
  points,
  ox,
  oy,
  sw,
  handleR,
  handleStrokeWidth,
  isPenEditMode,
  onElementDown,
  onElementMove,
  onElementUp,
}: NodeHandleDotsProps) {
  /** The handle dot's drag style + pointer handlers (none while in pen-edit mode). */
  const dotProps = (pt: PenAnchorPoint, type: 'handleIn' | 'handleOut') => ({
    style: { pointerEvents: isPenEditMode ? ('none' as const) : ('all' as const), cursor: 'grab' },
    onPointerDown: isPenEditMode
      ? undefined
      : (e: React.PointerEvent) => onElementDown(e, { type, index: points.indexOf(pt) }),
    onPointerMove: isPenEditMode ? undefined : onElementMove,
    onPointerUp: isPenEditMode ? undefined : onElementUp,
  })

  return (
    <>
      {points.map((pt) => {
        const cx = pt.x + ox
        const cy = pt.y + oy

        return (
          <React.Fragment key={`${pt.id}-h`}>
            {pt.handleIn && (
              <BezierHandleDot
                anchorX={cx}
                anchorY={cy}
                handleX={pt.handleIn.x + ox}
                handleY={pt.handleIn.y + oy}
                radius={handleR}
                color={NODE_ACCENT_COLOR}
                lineWidth={sw}
                dotStrokeWidth={handleStrokeWidth}
                dotProps={dotProps(pt, 'handleIn')}
              />
            )}
            {pt.handleOut && (
              <BezierHandleDot
                anchorX={cx}
                anchorY={cy}
                handleX={pt.handleOut.x + ox}
                handleY={pt.handleOut.y + oy}
                radius={handleR}
                color={NODE_ACCENT_COLOR}
                lineWidth={sw}
                dotStrokeWidth={handleStrokeWidth}
                dotProps={dotProps(pt, 'handleOut')}
              />
            )}
          </React.Fragment>
        )
      })}
    </>
  )
}
